import { Link } from 'react-router';
import { ChevronRight } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import { CONTENT_CAP_PADDED } from './large-screen';
import { DetailProfileStats } from './DetailProfileStats';
import type { DetailItemStats } from './detail-stats';

export interface DetailBreadcrumbStep {
    label: string;
    /** Absent on the step the reader is on. */
    to?: string;
}

type Theme = 'light' | 'dark';

/**
 * The trail's type, matched to the reference: 14px Roman, 16px between steps,
 * and the step you are on in the same grey. A quieter grey on the light page,
 * and on the dark page a step up from the ground so it still reads.
 */
const crumbClass = (theme: Theme) =>
    cn(
        'font-sans font-normal no-underline transition-colors max-md:text-[13px] md:text-[14px]',
        theme === 'dark'
            ? 'text-[#767676] hover:text-[#e5e5e5]'
            : // The body's own ink below md: the pale grey sat on the grey
              // ground unread.
              'text-[#b3b3b2] hover:text-[#767676] max-md:text-[#323232]',
    );

export const DetailBreadcrumb = ({
    trail,
    theme = 'light',
}: {
    trail: DetailBreadcrumbStep[];
    theme?: Theme;
}) => (
    <nav aria-label="Breadcrumb" className="mb-[12px] max-md:mb-0">
        {/* 8px between steps below md: at the 16px desktop gap a three-step
            trail wraps on a 414 measure. */}
        <ol className="m-0 flex list-none flex-wrap items-center gap-2 p-0 md:gap-[16px]">
            {trail.map((step, index) => (
                <li
                    key={`${step.label}-${index}`}
                    className="flex min-w-0 items-center gap-2 md:gap-[16px]"
                >
                    {index > 0 ? (
                        <ChevronRight
                            aria-hidden="true"
                            className={cn(
                                'size-[12px] shrink-0',
                                theme === 'dark'
                                    ? 'text-[#767676]'
                                    : 'text-[#b3b3b2] max-md:text-[#767676]',
                            )}
                        />
                    ) : null}
                    {step.to ? (
                        <Link to={step.to} className={crumbClass(theme)}>
                            {step.label}
                        </Link>
                    ) : (
                        <span aria-current="page" className={crumbClass(theme)}>
                            {step.label}
                        </span>
                    )}
                </li>
            ))}
        </ol>
    </nav>
);

export interface DetailBreadcrumbBarProps {
    trail: DetailBreadcrumbStep[];
    theme?: Theme;
    /** The bar's ground, for a page whose background is not white. */
    className?: string;
    /**
     * The professional's profile views and saves, ranged right on the
     * trail's line and 48px clear of the badge's column. Out of the flow on
     * desktop, so they cannot move the summary row.
     */
    stats?: DetailItemStats;
}

/**
 * The category trail, directly under the banner or gallery at the page
 * gutter, where a reader looks to find out where they are. 32px under the
 * picture on every detail page, so the badge above it and the summary row
 * below it land on the same pixels on each.
 */
export const DetailBreadcrumbBar = ({
    trail,
    theme = 'light',
    className,
    stats,
}: DetailBreadcrumbBarProps) => {
    if (trail.length === 0 && !stats) return null;

    return (
        <section className={cn('max-md:px-6 md:px-[3rem]', className)}>
            <div className={CONTENT_CAP_PADDED}>
                {/* Below md the trail keeps the whole line to itself and the
                    figures follow on their own — sharing the line squeezed a
                    three-step trail into a broken two-line wrap. */}
                <div className="flex items-start justify-between gap-6 max-md:flex-col max-md:gap-2 max-md:pt-6 md:relative md:pt-[2rem]">
                    {trail.length > 0 ? (
                        <DetailBreadcrumb trail={trail} theme={theme} />
                    ) : (
                        <span />
                    )}
                    {stats ? (
                        <DetailProfileStats
                            stats={stats}
                            theme={theme}
                            className="md:absolute md:top-[2rem] md:right-[16.5625rem]"
                        />
                    ) : null}
                </div>
            </div>
        </section>
    );
};
